/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React from "react";

import Link from "next/link";
import { isElectron } from "react-device-detect";
import styled from "styled-components";

import { DEVICE } from "src/app/globalStyles";
import { BreadcrumStem, LDBreadcrum } from "src/components/Splash/Layout/Typography";
import { StyledLink } from "src/components/UI/Links";
import { AppRoutes } from "src/lib/constants";

import { LDHeaderWrapper } from "./Layout/HeadFoot";

interface Props {
  text: string | React.ReactNode;
  textLink?: string;
  leftItem?: React.ReactNode;
  rightItem?: React.ReactNode;
  stem?: string | React.ReactNode; // default is "VIENNA /"
  stemLink?: AppRoutes; // default is AppRoutes.Meta
}

export function LDHeader({ text, leftItem, rightItem }: Props) {
  return (
    <LDHeaderWrapper>
      <ItemWrapper>{leftItem}</ItemWrapper>
      <PageTitleText>{text}</PageTitleText>
      <ItemWrapper>{rightItem}</ItemWrapper>
    </LDHeaderWrapper>
  );
}

export function LDHeaderWithBreadcrums({ text, textLink, leftItem, rightItem, stem, stemLink }: Props) {
  return (
    <LDHeaderWrapper>
      <ItemWrapper>{leftItem}</ItemWrapper>
      <PageTitleText>
        <Link passHref href={stemLink || AppRoutes.Meta}>
          <StyledLink>
            <BreadcrumStem>{stem || "VIENNA /"}</BreadcrumStem>
          </StyledLink>
        </Link>
        <Link passHref href={textLink!}>
          <StyledLink>{text}</StyledLink>
        </Link>
      </PageTitleText>
      <ItemWrapper>{rightItem}</ItemWrapper>
    </LDHeaderWrapper>
  );
}

export function JobPostHeader({ jobTitle }: { jobTitle: string }) {
  return (
    <LDHeader
      text={
        <>
          <Link href={AppRoutes.Jobs} passHref>
            <LDBreadcrum>JOBS /</LDBreadcrum>
          </Link>{" "}
          {jobTitle}
        </>
      }
      leftItem={
        <Link href={isElectron ? "/index.html" : "/"} passHref>
          <HeaderLink style={{ textAlign: "left", marginLeft: "20px" }}>HOME</HeaderLink>
        </Link>
      }
    />
  );
}

const PageTitleText = styled.h1`
  color: #000;
  font-size: 18px;
  font-weight: 400;
  line-height: 100%;
  margin-bottom: 0px;
  margin-top: 0px;

  @media (max-width: ${DEVICE.small}) {
    font-size: 12px;
  }
`;

const ItemWrapper = styled.div`
  width: 100px;

  @media (max-width: ${DEVICE.small}) {
    font-size: 12px;
    width: 70px;
  }
`;

export const HeaderLink = styled.a`
  color: #904d4d;
  cursor: pointer;
  display: block;
  font-size: 10px;
  margin-right: 20px;
  text-align: right;
  transition: opacity 200ms ease;
  text-decoration: none;
  -webkit-transition: opacity 200ms ease;
  :hover {
    color: #494949;
    text-decoration: underline;
  }
`;
